import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import * as SemanticComponents from 'semantic-ui-react';
import { CloudStorageService } from '~/api/services/cloudStorage';
import { El } from '~/components/Layout/style';
import notify from '~/components/Notify';
import {
  StyledRightSideWrapper,
  TableFooterWrapper
} from '../style';
import FileCard from '../FileCard';
import { ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG, optionsLimit } from '~/Constants';
import i18n from '~/components/i18n';
import { useAuth } from '~/routes/components/AuthProvider';
import { useDebounce } from 'use-lodash-debounce';
import { DownloadService } from '~/api/services/downloadFiles';
import DeleteFileModal from './Modals/DeleteFileModal';
import CreateEditFileModal from './Modals/CreateEditFileModal';
import PreviewFileModal from './Modals/PreviewFileModal';

interface FilesLayoutProps {
  id: string
  folderName: string
  optionsData: any
  type: string
  handleChangeFolders: (filesCount: any, id: string) => void
}

export type FileData = {
  createdAt: string;
  filename: string;
  filesize: number;
  filetype: string;
  id: string;
  isNew: number;
  title: string;
  views: number;
};

export type FileSrcType = {
  src: string | boolean,
  type?: string,
  createdAt?: string,
  size?: number,
  name?: string,
  id?: string
}

const getLeftSideConstItems = () => [
  {
    name: i18n.t('All'),
    icon: 'th',
    id: 'all',
    countKey: 'allCount'
  },
  {
    name: i18n.t('Images'),
    icon: 'images',
    id: 'image',
    countKey: 'imageCount'
  },
  {
    name: i18n.t('Videos'),
    icon: 'video play',
    id: 'video',
    countKey: 'videoCount'
  },
  {
    name: i18n.t('Documents'),
    icon: 'file alternate',
    id: 'document',
    countKey: 'documentCount'
  },
  {
    name: i18n.t('Other'),
    icon: 'archive',
    id: 'other',
    countKey: 'otherCount'
  }
]

const FilesLayout: React.FunctionComponent<FilesLayoutProps> = (props) => {
  const { folderName, id, optionsData } = props
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const [files, setFiles] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [openFilesModal, setOpenFilesModal] = useState(null)
  const [checkedFiles, setCheckedFiles] = useState([])
  const [fileSource, setFileSource] = useState<FileSrcType | null>(null)
  const [openDeleteFilesModal, setOpenDeleteFilesModal] = useState(false)
  const [search, setSearch] = useState('')
  const [singleDeleteFile, setSingleDeleteFile] = useState(null)
  const debouncedValue = useDebounce(search, 300);
  const [editFileId, setEditFileId] = useState(null)
  const editingRole = (roleSlug === ADMIN_ROLE_SLUG) || (roleSlug === OPERATIVE_ROLE_SLUG)
  const [count, setCount] = useState(null)
  const [dataFiltered, setDataFiltered] = useState({
    folderId: id,
    rows: 30,
    page: 0,
    search: '',
    type: 'all'
  })

  useEffect(() => {
    props.handleChangeFolders(files?.total, id)
  }, [files])

  useEffect(() => {
    if (dataFiltered.folderId !== props.id) {
      setDataFiltered({ ...dataFiltered, folderId: props.id, page: 0, search: '' })
    }
  }, [props.id])

  useEffect(() => {
    fetchFolderFiles()
  }, [dataFiltered])

  useEffect(() => {
    if (debouncedValue !== dataFiltered.search) {
      setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
    }
  }, [debouncedValue])

  function notInViewport(element: any, offset = 0) {
    if (!element) return false
    const top = element.getBoundingClientRect().top
    return ((top + offset) >= 0 && (top - offset + 100) >= window.innerHeight) || (top - 20 <= 0)
  }

  async function fetchFolderFiles() {
    setIsBusy(true);
    try {
      let responseList
      let responseCount
      if (dataFiltered.folderId === 'newFolder') {
        responseList = await CloudStorageService.folderWithNewFiles({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
        responseCount = await CloudStorageService.getCountWithNewFiles({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
      } else {
        responseList = await CloudStorageService.getFilesInFolder({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
        responseCount = await CloudStorageService.getCountFilesByType({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
      }
      setFiles(responseList.data)
      setCount(responseCount.data)
      const element = document.getElementById(dataFiltered.folderId)
      if (element && !notInViewport(element)) {
        element.innerText = `(${responseCount.data?.allCount})`
      }
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  const renderModalPreviewFile = () => {
    return <PreviewFileModal
      fileSource={fileSource}
      files={files}
      setFiles={setFiles}
      handleClose={() => setFileSource(null)}
      isBusy={isBusy}
      dataFiltered={dataFiltered}
    />
  };

  function renderActions() {
    return <>
      {checkedFiles.length > 0 && <El.PageHeaderActionButton
        onClick={() => setOpenDeleteFilesModal(true)}>{t('Delete files')}
      </El.PageHeaderActionButton>}
      <El.PageHeaderActionButton
        onClick={() => setOpenFilesModal(true)}>{t('Upload new file')}
      </El.PageHeaderActionButton>
    </>
  }

  function renderCreateEditFilesModal() {
    return <CreateEditFileModal
      openFilesModal={openFilesModal}
      handleClose={(reloadFiles) => {
        setEditFileId(null)
        setOpenFilesModal(false)
        if (reloadFiles) {
          fetchFolderFiles()
        }
      }}
      optionsData={optionsData}
      id={editFileId}
      folderId={dataFiltered.folderId}
    />
  }

  async function fetchPreviewData(id, file) {
    setIsBusy(true);
    setFileSource({ src: true })
    try {
      const response = await CloudStorageService.getTempLinkForFile(id, { fileName: file?.filename })
      setFileSource({
        src: response.data,
        type: file?.filetype,
        createdAt: file?.createdAt,
        size: file?.filesize,
        name: file?.filename,
        id
      })
      if (!editingRole) {
        await CloudStorageService.increaseViewCounter(id)
      }
    } catch (error) {
      setFileSource(null)
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  async function downloadFile(file: FileData) {
    setIsBusy(true);
    try {
      await DownloadService.downloadFile(`cloud-storage/files/${file?.id}/get-file`, file?.title, null, true)
      if (dataFiltered.folderId === 'newFolder') {
        const element = document.getElementById('newFolder')
        if (element) {
          element.innerText = `(${files?.rows?.length - 1})`
          setFiles({ ...files, rows: files?.rows?.filter(fileRow => fileRow.id !== file?.id) })
        }
      }
      if (!editingRole) {
        await CloudStorageService.increaseViewCounter(file?.id)
      }
    } catch (error) {
      setFileSource(null)
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  function handleActionClick(type, file) {
    switch (type) {
      case 'edit':
        setEditFileId(file?.id)
        setOpenFilesModal(true)
        break;
      case 'preview':
        fetchPreviewData(file?.id, file)
        break;
      case 'delete':
        setSingleDeleteFile(file)
        setOpenDeleteFilesModal(true)
        break;
      case 'download':
        downloadFile(file)
        break;
      case 'link':
        window.open(file?.link, '_blank', 'noopener,noreferrer')
        break;
      default:
        return
    }
  }

  function renderDeleteFilesModal() {
    return <DeleteFileModal
      handleSubmit={() => {
        setCheckedFiles([])
        setOpenDeleteFilesModal(false)
        setSingleDeleteFile(null)
        fetchFolderFiles()
      }}
      openDeleteFilesModal={openDeleteFilesModal}
      checkedFiles={checkedFiles}
      files={files}
      singleDeleteFile={singleDeleteFile}
      handleClose={() => setOpenDeleteFilesModal(false)}
    />
  }

  function renderSearch() {
    return <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
      <SemanticComponents.Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search files...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <SemanticComponents.Dropdown
        selection
        placeholder={t('Select type')}
        value={dataFiltered.type}
        options={getLeftSideConstItems().map(item => ({ value: item.id, text: `${item.name} (${count?.[item.countKey] || 0})`, icon: item.icon }))}
        selectOnBlur={false}
        //@ts-ignore
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, type: data.value })}
      />
    </div>
  }

  function renderNoResultFiles() {
    return <SemanticComponents.Message info style={{ margin: '20px auto', textAlign: 'center', maxHeight: 70 }}>
      <SemanticComponents.Message.Header>{t('No results')}</SemanticComponents.Message.Header>
      <p>{t('Sorry, there are no files to display.')}</p>
    </SemanticComponents.Message>
  }

  function renderFiles() {
    return files?.rows.map(file => {
      return <FileCard
        key={file.id}
        file={file}
        disableAction={!editingRole}
        onActionClick={(type: string) => handleActionClick(type, file)}
        onChecked={(checked, id) => {
          if (checked) {
            setCheckedFiles([...checkedFiles, id])
          } else {
            setCheckedFiles(checkedFiles.filter(idChecked => idChecked !== id))
          }
        }}
        checked={Boolean(checkedFiles.find(fileId => fileId === file.id))}
      />
    })
  }

  function renderPagination() {
    const { Icon } = SemanticComponents
    return <div>
      <SemanticComponents.Pagination
        onPageChange={(_, data) => setDataFiltered({ ...dataFiltered, page: parseInt(data.activePage.toString()) })}
        activePage={dataFiltered.page === 0 ? 1 : dataFiltered.page}
        ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
        firstItem={{ content: <Icon name='angle double left' />, icon: true }}
        lastItem={{ content: <Icon name='angle double right' />, icon: true }}
        prevItem={{ content: <Icon name='angle left' />, icon: true }}
        nextItem={{ content: <Icon name='angle right' />, icon: true }}
        totalPages={files?.filtered ? Math.ceil(files?.filtered / dataFiltered.rows) : 1} />
    </div>
  }

  function renderTableFooter() {
    const { Label } = SemanticComponents
    return <TableFooterWrapper>
      <div>
        <SemanticComponents.Dropdown
          basic
          button
          upward={true}
          size='small'
          value={dataFiltered.rows}
          onChange={(e, data) => {
            setDataFiltered({
              ...dataFiltered,
              rows: data.value as number
            })
          }}
          options={optionsLimit}
        />
        <Label basic pointing={'left'}>
          {t('Total: ')}
          <Label.Detail>{files?.total}</Label.Detail>
        </Label>
      </div>
      {renderPagination()}
    </TableFooterWrapper>
  }

  return <StyledRightSideWrapper>
    {renderDeleteFilesModal()}
    {openFilesModal && renderCreateEditFilesModal()}
    {renderModalPreviewFile()}
    <SemanticComponents.Dimmer inverted active={isBusy}>
      <SemanticComponents.Loader active={isBusy} />
    </SemanticComponents.Dimmer>
    <div className='headerFiles'>
      <div className='title'>
        <SemanticComponents.Popup
          content={folderName}
          trigger={<div className='titleHeader'>{folderName}</div>}
        />
        {renderSearch()}
      </div>
      <div>
        {editingRole && renderActions()}
      </div>
    </div>
    <div>
      <div className='content'>
        {files?.rows.length > 0 ? renderFiles() : renderNoResultFiles()}
      </div>
      {renderTableFooter()}
    </div>
  </StyledRightSideWrapper>
};

export default FilesLayout;
